import { PackageData } from '@/pages/CreatePackage';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { 
  User, MapPin, Calendar, Users, Hotel, Plane, 
  Car, MapPinned, Package, DollarSign, Check 
} from 'lucide-react';
import { calculateFullPriceBreakdown } from '@/utils/packageCalculations';

interface PackageSidebarSummaryProps {
  data: PackageData;
  completedSteps: number[];
}

export const PackageSidebarSummary = ({ data, completedSteps }: PackageSidebarSummaryProps) => {
  const priceBreakdown = calculateFullPriceBreakdown(data);
  
  const sections = [
    {
      step: 1,
      title: 'معلومات العميل',
      icon: User,
      items: [
        { label: 'نوع العميل', value: data.clientType === 'individual' ? 'فرد' : 'شركة' },
        { label: 'رقم الهاتف', value: data.clientPhone },
        data.clientName && { label: 'الاسم', value: data.clientName },
      ].filter(Boolean),
    },
    {
      step: 2,
      title: 'تفاصيل الرحلة',
      icon: MapPin,
      items: [
        { label: 'الوجهة', value: data.destination },
        { label: 'تاريخ الوصول', value: data.duration },
        { label: 'عدد الليالي', value: data.numberOfNights },
        { label: 'البالغون', value: data.adultsCount },
        data.childrenCount > 0 && { label: 'الأطفال', value: data.childrenCount },
        { label: 'الغرف', value: data.numberOfRooms },
      ].filter(Boolean),
    },
    {
      step: 3,
      title: 'الفنادق',
      icon: Hotel,
      items: data.includesHotels && data.hotels.length > 0
        ? [
            { label: 'عدد الفنادق', value: data.hotels.length },
            { label: 'إجمالي التكلفة', value: `${priceBreakdown.hotelsTotal.toLocaleString()} ريال` },
          ]
        : [{ label: 'الحالة', value: 'غير مشمول' }],
    },
    {
      step: 4,
      title: 'الطيران',
      icon: Plane,
      items: (data.includesInternationalFlights || data.includesDomesticFlights) && data.flights.length > 0
        ? [
            { label: 'عدد الرحلات', value: data.flights.length },
            { label: 'إجمالي التكلفة', value: `${priceBreakdown.flightsTotal.toLocaleString()} ريال` },
          ]
        : [{ label: 'الحالة', value: 'غير مشمول' }],
    },
    {
      step: 5,
      title: 'المواصلات',
      icon: Car,
      items: data.includesTransport && data.transport.length > 0
        ? [
            { label: 'عدد المركبات', value: data.transport.length },
            { label: 'إجمالي التكلفة', value: `${priceBreakdown.transportTotal.toLocaleString()} ريال` },
          ]
        : [{ label: 'الحالة', value: 'غير مشمول' }],
    },
    {
      step: 6,
      title: 'الجولات',
      icon: MapPinned,
      items: data.includesTours && data.tours.length > 0
        ? [
            { label: 'عدد الجولات', value: data.tours.length },
            { label: 'إجمالي التكلفة', value: `${priceBreakdown.toursTotal.toLocaleString()} ريال` },
          ]
        : [{ label: 'الحالة', value: 'غير مشمول' }],
    },
    {
      step: 7,
      title: 'الخدمات',
      icon: Package,
      items: data.includesServices && data.services.length > 0
        ? [
            { label: 'عدد الخدمات', value: data.services.length },
            { label: 'إجمالي التكلفة', value: `${priceBreakdown.servicesTotal.toLocaleString()} ريال` },
          ]
        : [{ label: 'الحالة', value: 'غير مشمول' }],
    },
    {
      step: 8,
      title: 'التسعير',
      icon: DollarSign,
      items: [
        { label: 'السعر الأساسي', value: `${priceBreakdown.basePrice.toLocaleString()} ريال` },
        { label: 'الربح', value: `${priceBreakdown.profitAmount.toLocaleString()} ريال` },
        priceBreakdown.discount > 0 && { label: 'الخصم', value: `${priceBreakdown.discount.toLocaleString()} ريال` },
        priceBreakdown.taxes > 0 && { label: 'الضرائب', value: `${priceBreakdown.taxes.toLocaleString()} ريال` },
      ].filter(Boolean),
    },
  ];

  return (
    <Card className="sticky top-4 shadow-lg border-primary/20">
      <CardHeader className="bg-gradient-to-r from-primary/10 to-primary/5 pb-4">
        <CardTitle className="text-lg flex items-center gap-2">
          <Package className="w-5 h-5 text-primary" />
          ملخص الباقة
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 space-y-4 max-h-[calc(100vh-200px)] overflow-y-auto">
        {sections.map((section) => {
          const IconComponent = section.icon;
          const isCompleted = completedSteps.includes(section.step);
          
          return (
            <div key={section.step} className={`space-y-2 ${isCompleted ? 'opacity-100' : 'opacity-50'}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <IconComponent className={`w-4 h-4 ${isCompleted ? 'text-primary' : 'text-muted-foreground'}`} />
                  <span className="text-sm font-semibold">{section.title}</span>
                </div>
                {isCompleted && (
                  <Check className="w-4 h-4 text-green-500" />
                )}
              </div>
              <div className="pr-6 space-y-1">
                {section.items.map((item: any, idx: number) => (
                  <div key={idx} className="flex justify-between text-xs">
                    <span className="text-muted-foreground">{item.label}:</span>
                    <span className="font-medium">{item.value}</span>
                  </div>
                ))}
              </div>
              {section.step < 8 && <Separator className="mt-2" />}
            </div>
          );
        })}
        
        {/* Total Price Badge */}
        <div className="pt-4 border-t-2 border-primary/20">
          <div className="flex items-center justify-between bg-primary/10 p-3 rounded-lg">
            <span className="text-sm font-bold">السعر الإجمالي:</span>
            <Badge className="bg-primary text-primary-foreground text-base px-3 py-1">
              {priceBreakdown.totalPrice.toLocaleString()} ريال
            </Badge>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
